<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useField, useForm } from 'vee-validate'
import htmlExecuteService from '@/services/ide/languages/html/execute.service'
import { useIdeStore } from '@/stores/ide.store'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'
import advanceCompilerService from '@/services/ide/advanceCompiler.service'

import reload from '@/assets/images/ide/reload.svg'
import backNavigationIcon from '@/assets/images/ide/chevron-left.svg'
import forwardNavigationIcon from '@/assets/images/ide/chevron-right.svg'
import BaseInput from '@/components/forms/BaseInput.vue'

const props = defineProps<{
  iframeRef?: HTMLIFrameElement | null
}>()

const ideStore = useIdeStore()

const advanceCompilerStore = useAdvanceCompilerStore()
const { handleSubmit } = useForm()
const { value: tabname } = useField<string>('name', '')

const showNavigationHistoryButtons = ref<boolean>(false)
const historyStack = ref<string[]>([])

const currentIframeSrc = computed(() => {
  return props.iframeRef || null
})

const isHtml = computed(() => {
  return ideStore.isHtml
})

/**
 * Handles the form submission, ensuring it only runs once.
 */
const onSubmit = handleSubmit(() => {
  showNavigationHistoryButtons.value = false
  if (tabname.value && tabname.value != '/index.html' && tabname.value != ideStore?.project?.home) {
    ideStore.setCurrentIframeSourceBrowser(tabname.value)

    if (currentIframeSrc.value) {
      currentIframeSrc.value.src = tabname.value
    }

    if (
      ideStore.advanceServiceRunning &&
      !tabname.value.includes(advanceCompilerStore.subDomainUrl as string)
    ) {
      advanceCompilerService.stopPods()
    }
  } else if (isHtml.value) {
    htmlExecuteService.tryExecute()
  }
})

/**
 * Finds the previous element of a target element in an array.
 * @param  arr - The array to search in.
 * @param  target - The element to search for.
 * @returns The previous element as a string, or null if the target is not found or if there is no previous element.
 */
function findPrevUrl(arr: string[], target: string): string | null {
  const index = arr.indexOf(target)

  if (index === -1 || index === 0) {
    // Target not found in the array or it is the first element
    return null
  }

  return arr[index - 1]
}

/**
 * Finds the next element of a target element in an array.
 * @param  arr - The array to search in.
 * @param  target - The element to search for.
 * @returns The next element as a string, or null if the target is not found or if there is no next element.
 */
function findNextUrl(arr: string[], target: string): string | null {
  const index = arr.indexOf(target)

  if (index === -1 || index === arr.length - 1) {
    // Target not found in the array or it is the last element
    return null
  }

  return arr[index + 1]
}

/**
 * go back in navigation stack
 */
const goback = () => {
  const backUrl = findPrevUrl(historyStack.value, tabname.value)
  if (backUrl) {
    if (currentIframeSrc.value?.contentWindow) {
      currentIframeSrc.value.contentWindow.location.href = backUrl
    }
  }
}

/**
 * go forward in navigation stack
 */
const goForward = () => {
  const nextUrl = findNextUrl(historyStack.value, tabname.value)

  if (nextUrl) {
    if (currentIframeSrc.value?.contentWindow) {
      currentIframeSrc.value.contentWindow.location.href = nextUrl
    }
  }
}

/**
 * reload iframe src
 */
const reloadIframe = () => {
  onSubmit()
}

/**
 * Injects a script into the iframe to listen for URL changes.
 */
const injectScript = () => {
  try {
    if (currentIframeSrc.value) {
      showNavigationHistoryButtons.value = true
      const scriptContent = `
      (function() {
        const sendMessageToParent = () => {
          window.parent.postMessage({
            type: 'iframe-url-changed',
            url: window.location.href,

          }, '*');
        };

        // Monitor changes to the URL
        let lastUrl = window.location.href;
        new MutationObserver(() => {
          const currentUrl = window.location.href;
          if (lastUrl !== currentUrl) {
            lastUrl = currentUrl;
            sendMessageToParent();
          }
        }).observe(document, { subtree: true, childList: true });

        // Initial message
        sendMessageToParent();
      })();
    `
      const scriptElement = document.createElement('script')
      scriptElement.textContent = scriptContent
      currentIframeSrc.value.contentWindow?.document.body.appendChild(scriptElement)
    }
  } catch (error) {
    historyStack.value = []
    showNavigationHistoryButtons.value = false
    ;() => {}
  }
}

/**
 * Handles the iframe load event to log the iframe location.
 */
const onIframeLoad = () => {
  if (
    (advanceCompilerStore.subDomainUrl || ideStore.currentIFrameSourceBrowser) &&
    ideStore.currentIFrameSourceBrowser != '/index.html' &&
    ideStore.currentIFrameSourceBrowser != ideStore?.project?.home
  ) {
    injectScript()
  }
}

watch(
  () => props.iframeRef,
  (newValue) => {
    if (newValue) {
      newValue.addEventListener('load', onIframeLoad)
    }
  }
)

watch(
  () => ideStore.currentIFrameSourceBrowser,
  (newValue) => {
    tabname.value = newValue
  }
)

watch(
  () => ideStore.advanceServiceRunning,
  () => {
    if (ideStore.advanceServiceRunning) {
      tabname.value = advanceCompilerStore.subDomainUrl || ''
    }
  }
)
watch(
  () => advanceCompilerStore.isPodInitializationCompleted,
  () => {
    tabname.value = advanceCompilerStore.subDomainUrl || ''
  }
)

watch(
  () => ideStore.advanceServiceRunning,
  (val) => {
    if (val) {
      reloadIframe()
    }
  }
)

watch(
  () => ideStore.advanceServiceRunning,
  () => {
    if (!ideStore.advanceServiceRunning) {
      reloadIframe()
    }
  }
)

watch(
  () => isHtml.value,
  (value) => {
    if (!value) {
      tabname.value = advanceCompilerStore.subDomainUrl || ''
    } else {
      tabname.value = ideStore.currentIFrameSourceBrowser || ''
    }
  }
)

onMounted(() => {
  if (!ideStore.isHtml && !ideStore.isAdvancedCompiler && !ideStore.isAdvancedCompilerDatabase) {
    tabname.value = ''
  }
  if (ideStore.isHtml && ideStore.currentIFrameSourceBrowser) {
    tabname.value = ideStore.currentIFrameSourceBrowser
  } else if (
    ideStore.isAdvancedCompiler &&
    advanceCompilerStore.subDomainUrl &&
    advanceCompilerStore.isPodInitializationCompleted
  ) {
    tabname.value = advanceCompilerStore.subDomainUrl || ''
  }

  if (props.iframeRef) {
    props.iframeRef.addEventListener('load', onIframeLoad)
  }

  window.addEventListener('message', (event) => {
    if (event.data.type === 'iframe-url-changed') {
      tabname.value = event.data.url
      historyStack.value.push(event.data.url)
    }
  })
})

onUnmounted(() => {
  if (props.iframeRef) {
    props.iframeRef.removeEventListener('load', onIframeLoad)
  }
})
</script>
<template>
  <div class="mt-2 flex flex-col">
    <div class="flex h-10 w-full flex-row bg-white">
      <div
        class="background-quaternary stroke-primary-default flex h-full w-auto items-center justify-between gap-1 p-2"
      >
        <button
          :disabled="!showNavigationHistoryButtons"
          :class="{ 'cursor-not-allowed': !showNavigationHistoryButtons }"
          @click="goback"
          class="focus:outline-none"
        >
          <img :src="backNavigationIcon" class="h-5 w-5" />
        </button>
        <button
          :disabled="!showNavigationHistoryButtons"
          :class="{ 'cursor-not-allowed': !showNavigationHistoryButtons }"
          @click="goForward"
          class="focus:outline-none"
        >
          <img :src="forwardNavigationIcon" class="h-5 w-5" />
        </button>
        <button @click="reloadIframe" class="focus:outline-none">
          <img :src="reload" class="h-5 w-5" />
        </button>
      </div>
      <div class="background-quaternary stroke-primary-default flex h-full flex-grow items-center">
        <form class="w-full" @submit.prevent="onSubmit">
          <div class="p-1">
            <BaseInput
              placeholder="localhost:8080"
              v-model="tabname"
              :isSmallLabel="true"
              :showLable="false"
              :isEnterKeySupport="true"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
